import React, { useState } from 'react';
import { Plus, Trash2, Package, FlaskConical, AlertCircle, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Input } from '@/components/ui/input';
import SearchableSelect from './SearchableSelect';
import RacikanRow from './RacikanRow';

const fmt = (n) => new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const getHargaBeli = (item) => item?.harga_rupiah || 0;
const getHargaJual = (item) => item?.harga_jual || (getHargaBeli(item) * 1.5);

export default function DiscoverySetItems({ items, botolList = [], tutupList = [], sprayList = [], bahanList = [], fullsetList = [], onChange, isMarketing = true, onItemRemove, isDatabaseMode = false }) {
  const [expandedItems, setExpandedItems] = useState({ 0: true });

  const toggleExpand = (idx) => {
    setExpandedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleAddItem = () => {
    const newIdx = (items || []).length;
    onChange([...(items || []), { racikan: [], nama_variant: '' }]);
    setExpandedItems(prev => ({ ...prev, [newIdx]: true }));
  };

  const handleRemoveItem = (idx) => {
    if (onItemRemove && items[idx]) onItemRemove(items[idx]);
    const newItems = [...(items || [])];
    newItems.splice(idx, 1);
    onChange(newItems);
  };

  const updateItem = (idx, data) => {
    const newItems = [...(items || [])];
    newItems[idx] = { ...newItems[idx], ...data };
    onChange(newItems);
  };

  return (
    <div className="space-y-4 bg-[#F9F5EC]/50 p-4 rounded-xl border border-[#E6D5B8]/60">
      {(items || []).map((item, idx) => {
        // Calculate total percentage
        const totalPercentage = (item.racikan || []).reduce((sum, r) => sum + (Number(r.persentase) || 0), 0);
        const isRacikanValid = totalPercentage === 100;

        const iBotol = botolList.find(b => b.id === item.botol_id);
        const iTutup = tutupList.find(t => t.id === item.tutup_id);
        const iSpray = sprayList.find(s => s.id === item.spray_id);
        const iFullset = fullsetList?.find(f => f.id === item.fullset_id);
        const ukuranLabel = iBotol ? (iBotol.ukuran_label_ml || 0) : (iFullset ? (parseFloat(iFullset.nama.match(/(\d+)\s*(?:ml|g|gr|gram)/i)?.[1]) || 0) : 0);

        let modalPack = 0;
        let jualPack = 0;
        if (item.is_fullset && iFullset) {
          modalPack = iFullset.harga_rupiah || 0;
          jualPack = iFullset.harga_jual || (modalPack * 1.5);
        } else {
          modalPack = getHargaBeli(iBotol) + getHargaBeli(iTutup) + getHargaBeli(iSpray);
          jualPack = getHargaJual(iBotol) + getHargaJual(iTutup) + getHargaJual(iSpray);
        }

        const iUkuranAktual = item.custom_ukuran_aktual_ml !== undefined ? item.custom_ukuran_aktual_ml : (iBotol?.ukuran_aktual_ml || ukuranLabel);
        const iUkuranDipakai = item.custom_ukuran_aktual_ml !== undefined ? iUkuranAktual : ukuranLabel;
        
        let modalRacikan = 0;
        let jualRacikan = 0;
        (item.racikan || []).forEach(r => {
          if (!r.bahan_id || !r.persentase) return;
          const vol = (Number(String(r.persentase).replace(',', '.')) / 100) * iUkuranDipakai;
          const isLiter = r.satuan === 'g';
          const hBeli = r.harga_per_satuan || 0;
          const hJual = r.harga_jual_satuan !== undefined ? r.harga_jual_satuan : (hBeli * 1.3);
          
          if (isLiter) {
            modalRacikan += vol * (hBeli / 1000);
            jualRacikan += vol * (hJual / 1000);
          } else {
            const sat = r.satuan || 'kg';
            const pMlBeli = (sat === 'kg' || sat === 'liter') ? hBeli / 1000 : hBeli;
            
            let pMlJual = hJual;
            if (r.custom_harga_per_unit != null && r.custom_harga_per_unit !== 0) {
              pMlJual = r.custom_harga_per_unit;
            } else {
              pMlJual = (sat === 'kg' || sat === 'liter') ? hJual / 1000 : hJual;
            }
            
            modalRacikan += pMlBeli * vol;
            jualRacikan += pMlJual * vol;
          }
        });

        return (
          <div key={idx} className="border border-[#E6D5B8] bg-white rounded-xl p-4 relative shadow-sm transition-all duration-200">
            <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 ${expandedItems[idx] ? 'mb-4 pb-3 border-b border-[#E6D5B8]/50' : ''}`}>
              <div className="flex items-center gap-2 w-full sm:w-auto flex-1">
                <div className="w-6 h-6 shrink-0 rounded-full bg-[#E6D5B8] flex items-center justify-center text-white text-xs font-bold shadow-sm">
                  {idx + 1}
                </div>
                <Input 
                  placeholder="Nama Variant (contoh: Day Cream)"
                  value={item.nama_variant || ''}
                  onChange={(e) => updateItem(idx, { nama_variant: e.target.value })}
                  className="font-bold text-[#8C6208] border-0 bg-slate-50/50 hover:bg-slate-100 focus-visible:ring-1 ring-[#8C6208] h-8 max-w-[300px] placeholder:font-normal placeholder:text-[#A39171]"
                />
              </div>
              <div className="flex items-center gap-2">
                {!isDatabaseMode && (
                  <button 
                    type="button"
                    onClick={() => handleRemoveItem(idx)}
                    className="text-red-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded-lg transition-colors shrink-0 flex items-center gap-1 text-xs font-medium"
                  >
                    <Trash2 className="w-3 h-3" /> <span className="hidden sm:inline">Hapus</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => toggleExpand(idx)}
                  className="p-1.5 text-slate-400 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  {expandedItems[idx] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {expandedItems[idx] && (
              <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                <div className="flex items-center justify-between mb-3 mt-4">
              <h4 className="text-sm font-bold text-[#302621] flex items-center gap-2">
                <Package strokeWidth={1.5} className="w-4 h-4 text-[#8C6208]" /> Kemasan
              </h4>
              <div className="flex bg-slate-100 p-1 rounded-xl">
                <button 
                  type="button"
                  onClick={() => updateItem(idx, { is_fullset: false, fullset_id: null })}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${!item.is_fullset ? 'bg-white text-[#8C6208] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Satuan
                </button>
                <button 
                  type="button"
                  onClick={() => updateItem(idx, { is_fullset: true })}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${item.is_fullset ? 'bg-[#8C6208] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                >
                  Fullset
                </button>
              </div>
            </div>

            {item.is_fullset ? (
              <div className="mb-5 bg-emerald-50/50 border border-emerald-100 rounded-xl p-4">
                <label className="text-xs font-semibold text-emerald-800 mb-1.5 block">Pilih Data Fullset</label>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="md:col-span-3">
                    <SearchableSelect
                      options={fullsetList}
                      value={item.fullset_id}
                      onChange={v => {
                        const f = fullsetList.find(x => x.id === v);
                        updateItem(idx, { 
                          fullset_id: v, 
                          botol_id: f?.botol_id || null,
                          tutup_id: f?.tutup_id || null,
                          spray_id: f?.spray_id || null
                        });
                      }}
                      placeholder="Cari fullset..."
                      displayKey="nama"
                      valueKey="id"
                    />
                    {isMarketing && iFullset && (
                      <div className="mt-3 p-3 bg-emerald-100/50 rounded-lg text-[11px] font-medium text-emerald-800 flex justify-between items-center border border-emerald-200/50">
                        <div>Harga Modal (Beli) Fullset: <span className="font-bold">Rp {fmt(getHargaBeli(iFullset))}</span></div>
                        <div>Harga Jual Fullset: <span className="font-bold">Rp {fmt(getHargaJual(iFullset))}</span></div>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-[#7A695B] mb-1.5 block uppercase tracking-wider">Ukuran Label (ml)</label>
                    <div className="h-10 flex items-center px-3 border border-[#E6D5B8] rounded-xl bg-[#F9F5EC] text-sm font-semibold text-[#302621]">
                      {ukuranLabel > 0 ? `${ukuranLabel} ml` : 'Pilih fullset'}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-blue-600 mb-1.5 block uppercase tracking-wider">Isi Aktual (ml)</label>
                    <Input 
                      type="number" 
                      min="0" 
                      placeholder={ukuranLabel > 0 ? ukuranLabel.toString() : "0"} 
                      value={item.custom_ukuran_aktual_ml !== undefined ? item.custom_ukuran_aktual_ml : (ukuranLabel > 0 ? ukuranLabel : '')} 
                      onChange={e => {
                        const val = e.target.value;
                        updateItem(idx, { 
                          custom_ukuran_aktual_ml: val === '' ? undefined : parseFloat(val)
                        });
                      }} 
                      className="h-10 border-blue-200 text-blue-700 bg-blue-50 focus:ring-blue-500 font-bold rounded-xl text-sm" 
                    />
                  </div>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-5">
              <div>
                <label className="text-[10px] font-bold text-[#7A695B] mb-1.5 block uppercase tracking-wider">Botol / Wadah</label>
                <SearchableSelect
                  options={botolList}
                  value={item.botol_id}
                  onChange={v => updateItem(idx, { botol_id: v })}
                  placeholder="Cari botol..."
                  displayKey="nama"
                  valueKey="id"
                />
                {isMarketing && iBotol && (
                  <div className="mt-2 text-[10px] text-[#A39171] flex justify-between px-1">
                    <span>Beli: Rp {fmt(getHargaBeli(iBotol))}</span>
                    <span>Jual: Rp {fmt(getHargaJual(iBotol))}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#7A695B] mb-1.5 block uppercase tracking-wider">Tutup (Cap)</label>
                <SearchableSelect
                  options={tutupList}
                  value={item.tutup_id}
                  onChange={v => updateItem(idx, { tutup_id: v })}
                  placeholder="Cari tutup..."
                  displayKey="nama"
                  valueKey="id"
                />
                {isMarketing && item.tutup_id && (() => {
                  const t = tutupList.find(x => x.id === item.tutup_id);
                  return t ? (
                    <div className="mt-2 text-[10px] text-[#A39171] flex justify-between px-1">
                      <span>Beli: Rp {fmt(getHargaBeli(t))}</span>
                      <span>Jual: Rp {fmt(getHargaJual(t))}</span>
                    </div>
                  ) : null;
                })()}
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#7A695B] mb-1.5 block uppercase tracking-wider">Spray / Pump</label>
                <SearchableSelect
                  options={sprayList}
                  value={item.spray_id}
                  onChange={v => updateItem(idx, { spray_id: v })}
                  placeholder="Cari spray..."
                  displayKey="nama"
                  valueKey="id"
                />
                {isMarketing && item.spray_id && (() => {
                  const s = sprayList.find(x => x.id === item.spray_id);
                  return s ? (
                    <div className="mt-2 text-[10px] text-[#A39171] flex justify-between px-1">
                      <span>Beli: Rp {fmt(getHargaBeli(s))}</span>
                      <span>Jual: Rp {fmt(getHargaJual(s))}</span>
                    </div>
                  ) : null;
                })()}
              </div>
              <div>
                <label className="text-[10px] font-bold text-[#7A695B] mb-1.5 block uppercase tracking-wider">Ukuran Label (ml)</label>
                <div className="h-10 flex items-center px-3 border border-[#E6D5B8] rounded-xl bg-[#F9F5EC] text-sm font-semibold text-[#302621]">
                  {ukuranLabel > 0 ? `${ukuranLabel} ml` : 'Pilih botol'}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold text-blue-600 mb-1.5 block uppercase tracking-wider">Isi Aktual (ml)</label>
                <Input 
                  type="number" 
                  min="0" 
                  placeholder={ukuranLabel > 0 ? ukuranLabel.toString() : "0"} 
                  value={item.custom_ukuran_aktual_ml !== undefined ? item.custom_ukuran_aktual_ml : (ukuranLabel > 0 ? ukuranLabel : '')} 
                  onChange={e => {
                    const val = e.target.value;
                    updateItem(idx, { 
                      custom_ukuran_aktual_ml: val === '' ? undefined : parseFloat(val)
                    });
                  }} 
                  className="h-10 border-blue-200 text-blue-700 bg-blue-50 focus:ring-blue-500 font-bold rounded-xl text-sm" 
                />
              </div>
            </div>
            )}
            
            <div className="bg-white border border-[#E6D5B8] rounded-xl overflow-hidden shadow-sm">
               <div className="flex items-center justify-between p-3 sm:px-5 sm:py-4 bg-[#F9F5EC] border-b border-[#E6D5B8]">
                 <label className="text-sm font-bold text-[#302621] flex items-center gap-2">
                   <FlaskConical className="w-4 h-4 text-[#8C6208]" /> 
                   Komposisi Cairan 
                   <span className="bg-white px-2 py-0.5 rounded text-[10px] text-[#7A695B] font-normal border border-[#E6D5B8]">
                     Acuan: {ukuranLabel || 0}ml
                   </span>
                 </label>
                 
                 <div className="flex items-center gap-3">
                   <div className={`text-xs font-bold px-2 py-1 rounded-md ${isRacikanValid ? 'bg-green-100 text-green-700' : 'bg-[#FDF6E3] text-[#8C6208]'}`}>
                     {totalPercentage.toFixed(1)}% / 100%
                   </div>
                   {!isDatabaseMode && (
                     <button 
                        type="button"
                        onClick={() => {
                          const r = [...(item.racikan || []), { kategori: 'Bibit', bahan_id: null, vendor_id: null, persentase: 0 }];
                          updateItem(idx, { racikan: r });
                        }}
                        className="text-xs font-bold bg-[#302621] text-white px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-[#4A3B32] transition-colors"
                     >
                       <Plus className="w-3 h-3" /> Tambah Cairan
                     </button>
                   )}
                 </div>
               </div>
               
               <div className="p-3 sm:p-5 bg-[#FFFDF9]">
                 {(!iBotol && !item.is_fullset && !iFullset) && (
                   <div className="bg-[#FFF8E6] border border-[#FBE3A1] text-[#B07D10] px-3 py-2 rounded-xl text-[11px] font-medium flex items-start gap-2 mb-3">
                     <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                     <p>Pilih botol terlebih dahulu agar kami bisa menghitung takaran volume otomatis.</p>
                   </div>
                 )}
                 
                 <div className="space-y-3">
                   {(!item.racikan || item.racikan.length === 0) ? (
                     <div className="text-center text-xs text-[#7A695B] py-6 border border-dashed border-[#E6D5B8] rounded-xl bg-white">
                       Belum ada komposisi cairan. Klik tombol "+ Tambah Cairan" untuk mulai meracik.
                     </div>
                   ) : (
                     item.racikan.map((racik, rIdx) => (
                       <RacikanRow
                         key={rIdx}
                         row={racik}
                         index={rIdx}
                         bahanList={bahanList}
                         ukuranMl={ukuranLabel}
                         readOnly={isDatabaseMode}
                         onUpdate={(rIdxToUpdate, data) => {
                           const newR = [...item.racikan];
                           newR[rIdxToUpdate] = { ...newR[rIdxToUpdate], ...data };
                           updateItem(idx, { racikan: newR });
                         }}
                         onRemove={(rIdxToRemove) => {
                           const newR = [...item.racikan];
                           newR.splice(rIdxToRemove, 1);
                           updateItem(idx, { racikan: newR });
                         }}
                       />
                     ))
                   )}
                 </div>
               </div>
            </div>
            
            {/* Ringkasan Harga Varian */}
            <div className="mt-4 bg-[#F9F5EC] border border-[#E6D5B8] rounded-xl p-4">
              <h5 className="text-xs font-bold text-[#8C6208] uppercase tracking-wider mb-3 border-b border-[#E6D5B8]/50 pb-2 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                Ringkasan Modal Variant Ini
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5 text-xs">
                  <div className="flex justify-between items-center text-[#7A695B]">
                    <span>Modal Kemasan</span>
                    <span>Rp {fmt(modalPack)}</span>
                  </div>
                  <div className="flex justify-between items-center text-[#7A695B]">
                    <span>Modal Cairan ({iUkuranDipakai}ml)</span>
                    <span>Rp {fmt(modalRacikan)}</span>
                  </div>
                  <div className="flex justify-between items-center font-bold text-[#302621] border-t border-[#E6D5B8]/50 pt-1.5 mt-1.5">
                    <span>Total Modal (per botol)</span>
                    <span>Rp {fmt(modalPack + modalRacikan)}</span>
                  </div>
                </div>
                {isMarketing && (
                  <div className="space-y-1.5 text-xs sm:border-l border-[#E6D5B8] sm:pl-4">
                    <div className="flex justify-between items-center text-[#7A695B]">
                      <span>Estimasi Jual Kemasan</span>
                      <span>Rp {fmt(jualPack)}</span>
                    </div>
                    <div className="flex justify-between items-center text-[#7A695B]">
                      <span>Estimasi Jual Cairan</span>
                      <span>Rp {fmt(jualRacikan)}</span>
                    </div>
                    <div className="flex justify-between items-center font-bold text-[#302621] border-t border-[#E6D5B8]/50 pt-1.5 mt-1.5">
                      <span>Total Jual (per botol)</span>
                      <span>Rp {fmt(jualPack + jualRacikan)}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          )}
          </div>
        );
      })}
      
      <button 
        type="button"
        onClick={handleAddItem}
        className="w-full py-3 mt-4 border-2 border-dashed border-[#E6D5B8] text-[#8C6208] font-bold text-sm rounded-xl hover:bg-[#F9F5EC] transition-colors flex items-center justify-center gap-2"
      >
        <Plus className="w-4 h-4" /> Tambah Variant/Botol Ke-{ (items || []).length + 1 }
      </button>
    </div>
  );
}
